/**
 * Inline proposal card rendered when the agent calls `propose_memory`.
 *
 * Three buttons: this session / me / team (the last opens a confirmation
 * modal because team-scope is teammate-visible). A skip button closes the
 * card without writing anything. After confirm or skip the card collapses
 * to a one-line status, mirroring `ClarifyingQuestionCard`'s answered state.
 *
 * Identity / scope_key resolution happens server-side: we only send the
 * scope (session | user | org) and let the message-gateway bind scope_key
 * from the verified token. For session scope we pass the chat sessionId
 * from props since HTTP doesn't otherwise carry it.
 */

import {
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import { useEffect, useMemo, useState } from 'react';

import { useConfirmMemoryProposal, useMemoryList } from '../api/memoryQueries';
import { memoryProposalSchema, type MemoryProposal, type MemoryScope } from '../api/memoryTypes';

/**
 * Strip the namespace's ``tool_end`` envelope (``{ text: '<json>' }``) and
 * return the inner payload as an object the Zod validator can consume.
 * Tolerant: if ``raw`` is already an object (older test fixtures, payloads
 * that bypassed the namespace) it passes through; if the JSON parse fails
 * we return the raw value so the validator gives the canonical error.
 */
const unwrapToolResult = (raw: unknown): unknown => {
  if (raw && typeof raw === 'object' && !Array.isArray(raw)) {
    const text = (raw as Record<string, unknown>).text;
    if (typeof text === 'string') {
      try {
        return JSON.parse(text);
      } catch {
        return raw;
      }
    }
    return raw;
  }
  if (typeof raw === 'string') {
    try {
      return JSON.parse(raw);
    } catch {
      return raw;
    }
  }
  return raw;
};

interface MemoryProposalCardProps {
  /** Raw `result` payload from the agent's tool call. Validated here. */
  proposalResult: unknown;
  /** Chat session id, required when the user picks 'this session'. */
  sessionId?: string | null;
  disabled?: boolean;
}

type CardState =
  | { kind: 'pending' }
  | { kind: 'confirmed'; scope: MemoryScope }
  | { kind: 'skipped' }
  | { kind: 'error'; message: string };

const SCOPE_LABEL: Record<MemoryScope, string> = {
  session: 'this session',
  user: 'me',
  org: 'your team',
};

export const MemoryProposalCard = ({
  proposalResult,
  sessionId,
  disabled,
}: MemoryProposalCardProps) => {
  const proposal = useMemo<MemoryProposal | null>(() => {
    // The wire shape is the namespace's `tool_end` envelope:
    //   { text: '<json-encoded payload>' }
    // The agent returns `propose_memory` as a JSON string for exactly this
    // reason — Strands' Python-repr fallback otherwise produces single-
    // quoted text Zod can't parse. Unwrap-then-parse handles both the
    // wrapped form (live tool result) and the bare-object form (test
    // fixtures, replayed events that already lost the envelope).
    const candidate = unwrapToolResult(proposalResult);
    const parsed = memoryProposalSchema.safeParse(candidate);
    return parsed.success ? parsed.data : null;
  }, [proposalResult]);

  const [state, setState] = useState<CardState>({ kind: 'pending' });
  const [orgConfirmOpen, setOrgConfirmOpen] = useState(false);
  const confirmMutation = useConfirmMemoryProposal();

  // Recover the answered/confirmed state on session replay. The card has
  // no client-side memory of "the user clicked Approve" across page
  // reloads, so a freshly-rehydrated proposal would otherwise render with
  // its action buttons even though the user already saved the memory.
  // Cross-reference the proposal against the user's memory list (which
  // is cached by react-query, so this is cheap when the MemoryPanel has
  // already loaded it) — a matching entry by ``kind + text`` means the
  // proposal was confirmed.
  const memoryList = useMemoryList({ sessionId, enabled: !!proposal });
  useEffect(() => {
    if (!proposal) return;
    if (state.kind !== 'pending') return;
    const list = memoryList.data ?? [];
    const match = list.find((m) => m.kind === proposal.kind && m.text === proposal.text);
    if (match) {
      setState({ kind: 'confirmed', scope: match.scope });
    }
  }, [proposal, memoryList.data, state.kind]);

  if (!proposal) {
    // The tool call returned an error envelope (e.g. blank text rejected
    // server-side) — render nothing; the underlying ToolCallCard already
    // surfaces the error in its own row.
    return null;
  }

  const submit = (scope: MemoryScope) => {
    confirmMutation.mutate(
      {
        kind: proposal.kind,
        text: proposal.text,
        scope,
        rationale: proposal.rationale ?? undefined,
        sessionId: scope === 'session' ? sessionId ?? undefined : undefined,
      },
      {
        onSuccess: () => setState({ kind: 'confirmed', scope }),
        onError: (err) =>
          setState({
            kind: 'error',
            message: err instanceof Error ? err.message : 'failed to save memory',
          }),
      }
    );
  };

  if (state.kind === 'confirmed') {
    return (
      <Paper
        variant="outlined"
        sx={{ padding: 1.25, borderRadius: 1, backgroundColor: 'action.hover' }}
        data-testid="memory-proposal-card-confirmed"
      >
        <Typography variant="caption" color="text.secondary" display="block">
          Saved to memory ({SCOPE_LABEL[state.scope]})
        </Typography>
        <Typography variant="body2">"{proposal.text}"</Typography>
      </Paper>
    );
  }
  if (state.kind === 'skipped') {
    return (
      <Paper
        variant="outlined"
        sx={{ padding: 1, borderRadius: 1, backgroundColor: 'action.hover' }}
        data-testid="memory-proposal-card-skipped"
      >
        <Typography variant="caption" color="text.secondary">
          Skipped: "{proposal.text}"
        </Typography>
      </Paper>
    );
  }
  if (state.kind === 'error') {
    return (
      <Paper
        variant="outlined"
        sx={{ padding: 1.5, borderRadius: 1, borderColor: 'error.main' }}
        data-testid="memory-proposal-card-error"
      >
        <Typography variant="caption" color="error.main">
          Could not save: {state.message}
        </Typography>
        <Button size="small" onClick={() => setState({ kind: 'pending' })}>
          Try again
        </Button>
      </Paper>
    );
  }

  const submitting = confirmMutation.isPending;

  return (
    <>
      <Paper
        variant="outlined"
        sx={{ padding: 1.5, borderRadius: 1, borderColor: 'primary.main' }}
        data-testid="memory-proposal-card"
      >
        <Stack direction="row" spacing={1} alignItems="center" sx={{ marginBottom: 1 }}>
          <Typography variant="body2" fontWeight={600}>
            Remember this?
          </Typography>
          <Chip size="small" variant="outlined" label={proposal.kind} />
        </Stack>
        <Typography variant="body2" sx={{ marginBottom: 1, fontStyle: 'italic' }}>
          "{proposal.text}"
        </Typography>
        {proposal.rationale && (
          <Typography
            variant="caption"
            color="text.secondary"
            display="block"
            sx={{ marginBottom: 1.25 }}
          >
            {proposal.rationale}
          </Typography>
        )}
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Button
            size="small"
            variant={proposal.suggested_scope === 'session' ? 'contained' : 'outlined'}
            disabled={disabled || submitting || !sessionId}
            onClick={() => submit('session')}
            data-testid="memory-proposal-save-session"
          >
            this session
          </Button>
          <Button
            size="small"
            variant={proposal.suggested_scope === 'user' ? 'contained' : 'outlined'}
            disabled={disabled || submitting}
            onClick={() => submit('user')}
            data-testid="memory-proposal-save-user"
          >
            me
          </Button>
          <Button
            size="small"
            variant="outlined"
            disabled={disabled || submitting}
            onClick={() => setOrgConfirmOpen(true)}
            data-testid="memory-proposal-save-org"
          >
            your team
          </Button>
          <Button
            size="small"
            variant="text"
            disabled={disabled || submitting}
            onClick={() => setState({ kind: 'skipped' })}
            sx={{ marginLeft: 'auto' }}
            data-testid="memory-proposal-skip"
          >
            skip
          </Button>
        </Stack>
      </Paper>

      <Dialog open={orgConfirmOpen} onClose={() => setOrgConfirmOpen(false)}>
        <DialogTitle>Share with your team?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            This memory will be visible to everyone in your organization. Make sure it's a team-wide
            rule, not just your own preference.
          </DialogContentText>
          <Box sx={{ marginTop: 1.5, padding: 1.5, backgroundColor: 'action.hover' }}>
            <Typography variant="body2" fontStyle="italic">
              "{proposal.text}"
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOrgConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => {
              setOrgConfirmOpen(false);
              submit('org');
            }}
            data-testid="memory-proposal-confirm-org"
          >
            Share with team
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
